// §6.8 Lagoon icon set — a spartan stroke family (pin, fish, map, user,
// plus, chev, close, share, camera, pencil, badge, warning, check) drawn
// on a 24×24 canvas with currentColor + 1.8 stroke. Chrome emoji from
// Phase 0 are all replaced by these; species-content emoji (🐟 fallback)
// move to `SceneFallback`.
//
// Silhouettes for `SceneFallback` (turtle, manta, fish1, fish2) render in
// --abyss so they read as habitat photography stand-ins — spec §7.
//
// Every icon accepts a className and any SVG prop; size defaults to 1em
// so parents can drive it with text-size / w-* / h-* utilities.

import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const base: IconProps = {
  width: "1em",
  height: "1em",
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

export function SearchIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5L20 20" />
    </svg>
  );
}

export function PinIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M12 21s-7-5.6-7-11a7 7 0 1 1 14 0c0 5.4-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  );
}

export function FishIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M3 12s3.2-5 8-5c3.6 0 6.4 2.6 8 5-1.6 2.4-4.4 5-8 5-4.8 0-8-5-8-5z" />
      <path d="M19 12l3.4-2.8v5.6L19 12z" fill="currentColor" stroke="none" />
      <circle cx="8.4" cy="11" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function MapIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M9 4 3.5 6v14L9 18l6 2 5.5-2V4L15 6 9 4z" />
      <path d="M9 4v14M15 6v14" />
    </svg>
  );
}

export function UserIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 20c1.2-3.4 3.8-5 7-5s5.8 1.6 7 5" />
    </svg>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <svg {...base} {...props} strokeWidth={2.4}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function ChevIcon(props: IconProps) {
  return (
    <svg {...base} {...props} strokeWidth={2}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <svg {...base} {...props} strokeWidth={2}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <svg {...base} {...props} strokeWidth={2.2}>
      <path d="M5 12l4.5 4.5L19 7" />
    </svg>
  );
}

export function ShareIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="18" cy="5" r="2.4" />
      <circle cx="6" cy="12" r="2.4" />
      <circle cx="18" cy="19" r="2.4" />
      <path d="M8 11l8-5M8 13l8 5" />
    </svg>
  );
}

export function CameraIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M5 8h3l1.5-2.4h5L16 8h3a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5v-8A1.5 1.5 0 0 1 5 8z" />
      <circle cx="12" cy="13.2" r="3.6" />
    </svg>
  );
}

export function PencilIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 20l1-4L16 5l3 3L8 19l-4 1z" />
    </svg>
  );
}

export function BadgeIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="10" r="6" />
      <path d="M9 15l-1 6 4-2 4 2-1-6" />
    </svg>
  );
}

export function WarningIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M12 3l10 18H2L12 3z" />
      <path d="M12 10v5" />
      <circle cx="12" cy="18" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function TripsIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <path d="M4 8h13l3 4-3 4H4z" />
      <path d="M4 8v-2h9v2" />
    </svg>
  );
}

export function MailIcon(props: IconProps) {
  return (
    <svg {...base} {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

// ---- silhouettes for SceneFallback -------------------------------------
// Rendered `fill: var(--abyss)` on top of a lagoon gradient so they read
// as habitat art rather than icons. viewBox intrinsic to each shape.

type SilhouetteProps = SVGProps<SVGSVGElement>;

const silBase: SilhouetteProps = {
  fill: "currentColor",
  "aria-hidden": true,
};

export function TurtleSil(props: SilhouetteProps) {
  return (
    <svg {...silBase} {...props} viewBox="0 0 120 150">
      <path d="M60 4c7 0 12 5 12 12 0 5-3 9-6 11 1 2 2 5 2 8H52c0-3 1-6 2-8-3-2-6-6-6-11 0-7 5-12 12-12z" />
      <path d="M60 34c19 0 31 16 31 42s-12 46-31 46-31-20-31-46 12-42 31-42z" />
      <path d="M31 52C19 43 6 44 3 54c9 7 21 9 31 7-2-3-3-6-3-9z" />
      <path d="M89 52c12-9 25-8 28 2-9 7-21 9-31 7 2-3 3-6 3-9z" />
      <path d="M38 114c-8 1-14 6-15 12 8 3 16 0 21-5-3-2-4-4-6-7z" />
      <path d="M82 114c8 1 14 6 15 12-8 3-16 0-21-5 3-2 4-4 6-7z" />
    </svg>
  );
}

export function MantaSil(props: SilhouetteProps) {
  return (
    <svg {...silBase} {...props} viewBox="0 0 150 132">
      <path d="M75 14c3 0 6 3 8 7 18-3 44 9 58 32-22-2-38 4-48 14-4 16-11 27-18 31-7-4-14-15-18-31-10-10-26-16-48-14 14-23 40-35 58-32 2-4 5-7 8-7z" />
      <path d="M68 15c-2-4-5-6-8-7 1 4 3 7 6 9zM82 15c2-4 5-6 8-7-1 4-3 7-6 9z" />
      <path d="M73 96h4c0 12-1 22-2 30-1-8-2-18-2-30z" />
    </svg>
  );
}

export function Fish1Sil(props: SilhouetteProps) {
  return (
    <svg {...silBase} {...props} viewBox="0 0 130 84">
      <path d="M12 42C28 16 64 10 88 28c6 5 10 10 14 14-4 4-8 9-14 14-24 18-60 12-76-14z" />
      <path d="M102 42l22-15-7 15 7 15-22-15z" />
      <path d="M44 16C52 6 68 6 76 14L60 24 44 16z" />
    </svg>
  );
}

export function Fish2Sil(props: SilhouetteProps) {
  return (
    <svg {...silBase} {...props} viewBox="0 0 140 60">
      <path d="M8 30C26 12 74 8 100 24l18-8-6 14 6 14-18-8C74 52 26 48 8 30z" />
    </svg>
  );
}

// Cetacean silhouette — used for dolphins, porpoises, whales. Long
// torpedo body, dorsal fin, split fluke tail. Distinct from the two
// generic fish shapes so a spinner dolphin card doesn't read as a fish.
export function DolphinSil(props: SilhouetteProps) {
  return (
    <svg {...silBase} {...props} viewBox="0 0 160 70">
      {/* body — long streamlined arc from rostrum to peduncle */}
      <path d="M10 40C28 22 70 18 108 26c10 2 18 5 24 9-6 4-14 7-24 9-38 8-80 4-98-14z" />
      {/* dorsal fin */}
      <path d="M78 20c4-10 12-14 18-10-2 6-6 10-14 12z" />
      {/* fluke (tail) */}
      <path d="M132 35l22-14-6 14 6 14-22-14z" />
      <path d="M132 35l22 14-6-14 6-14-22 14z" />
    </svg>
  );
}
